/**
 * ResultCard Component
 * Card displaying AI action result with file attachments support
 * 
 * Features:
 * - Shows text result
 * - Shows attached files (PDF, images, videos, etc.)
 * - File previews/icons based on type
 * - "Usar resposta" copies text + files
 * - History mode (no regeneration button)
 * - ✅ FIX: Altura dinâmica baseada no conteúdo
 */
import React, { useState } from 'react';
import {
  ArrowDownToLine,
  RefreshCcw,
  Loader2,
  Trash2,
  X,
  Languages,
  Sparkles,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  File,
  ExternalLink,
  Paperclip,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorkspaceColors } from './styles';
import { ACTIONS_CONFIG, MAX_REGENERATIONS, ACTION_TYPES } from './constants';

// File type icons
const FILE_TYPE_ICONS = {
  pdf: FileText,
  image: ImageIcon,
  video: Video,
  audio: Music,
  document: FileText,
  file: File,
};

// File type colors
const FILE_TYPE_COLORS = {
  pdf: '#E53935',
  image: '#43A047',
  video: '#1E88E5',
  audio: '#8E24AA',
  document: '#FB8C00',
  file: '#757575',
};

const ResultCard = ({ 
  result,
  onUseResponse,
  onRegenerate,
  remainingRegenerations = MAX_REGENERATIONS,
  isRegenerating = false,
  onRemove,
  isHistoryItem = false,
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const [imageError, setImageError] = useState({});

  const actionConfig = ACTIONS_CONFIG[result.action];
  const badgeColor = actionConfig?.badgeColor || { 
    bg: 'rgba(100, 100, 100, 0.2)', 
    text: '#888' 
  };

  const canRegenerate = !isHistoryItem && remainingRegenerations > 0 && !isRegenerating;
  
  // Get icon for action type
  const getActionIcon = () => {
    if (result.action === ACTION_TYPES.TRANSLATE) return Languages;
    if (result.action === ACTION_TYPES.CUSTOM) return Sparkles;
    return Sparkles;
  };
  const ActionIcon = getActionIcon();
  
  // Get files from result
  const files = result.files || [];
  const hasFiles = files.length > 0;

  const handleUseResponse = () => {
    onUseResponse?.(result);
  };

  const handleRegenerate = () => {
    if (!canRegenerate) return;
    onRegenerate?.(result);
  };

  const handleRemove = () => {
    onRemove?.(result);
  };

  const handleOpenFile = (file, e) => {
    e.stopPropagation();
    window.open(file.url, '_blank');
  };

  const cardStyles = {
    // ✅ FIX: Container com altura dinâmica
    container: {
      flex: '0 0 auto', // ✅ FIX: Era '0 0 320px', agora é auto
      width: 320, // Largura fixa
      minWidth: 360,
      maxWidth: 380,
      backgroundColor: workspaceColors.resultCardBg || workspaceColors.bgSecondary,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: isHistoryItem 
        ? `${workspaceColors.textTertiary}40` 
        : workspaceColors.borderColor,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      opacity: isHistoryItem ? 0.85 : 1,
      // ✅ FIX: Sem altura fixa - altura determinada pelo conteúdo
      alignSelf: 'flex-start', // ✅ FIX: Não estica verticalmente
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      backgroundColor: workspaceColors.bgTertiary,
      borderBottom: `1px solid ${workspaceColors.borderColor}`,
      flexShrink: 0,
    },
    languageInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    languageIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 26,
      height: 26,
      borderRadius: 6,
      backgroundColor: `${workspaceColors.accentPrimary}18`,
      color: workspaceColors.accentPrimary,
      flexShrink: 0,
    },
    languageName: {
      fontSize: 14,
      fontWeight: 600,
      color: workspaceColors.textPrimary,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    badge: {
      padding: '4px 10px',
      borderRadius: 6,
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      backgroundColor: badgeColor.bg,
      color: badgeColor.text,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    removeBtn: {
      padding: 6,
      border: 'none',
      background: 'transparent',
      color: workspaceColors.textTertiary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      transition: 'all 0.15s ease',
    },
    // ✅ FIX: Body com altura dinâmica e max-height
    body: {
      padding: 16,
      // ✅ FIX: Removido flex: 1
      maxHeight: 100, // ✅ Altura máxima para o texto
      overflowY: 'auto',
      flexShrink: 0,
    },
    text: {
      fontSize: 14,
      lineHeight: 1.6,
      color: workspaceColors.textPrimary,
      margin: 0,
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
    },
    // Files section
    filesSection: {
      padding: '0 14px 12px',
      borderTop: `1px solid ${workspaceColors.borderColor}`,
      backgroundColor: workspaceColors.bgTertiary,
      flexShrink: 0,
    },
    filesHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '10px 0 8px',
      fontSize: 11,
      fontWeight: 600,
      color: workspaceColors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    filesList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    fileItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 10px',
      backgroundColor: workspaceColors.bgPrimary,
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    },
    fileIcon: {
      width: 36,
      height: 36,
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    fileImagePreview: {
      width: 36,
      height: 36,
      borderRadius: 6,
      objectFit: 'cover',
    },
    fileInfo: {
      flex: 1,
      minWidth: 0,
    },
    fileName: {
      fontSize: 12,
      fontWeight: 500,
      color: workspaceColors.textPrimary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    fileType: {
      fontSize: 10,
      color: workspaceColors.textTertiary,
      textTransform: 'uppercase',
    },
    fileAction: {
      padding: 4,
      color: workspaceColors.textTertiary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Actions
    actions: {
      display: 'flex',
      gap: 8,
      padding: '12px 14px',
      borderTop: `1px solid ${workspaceColors.borderColor}`,
      backgroundColor: workspaceColors.bgTertiary,
      flexShrink: 0,
      marginTop: 'auto', // ✅ FIX: Empurra para baixo
    },
    useBtn: {
      flex: isHistoryItem ? 1 : 3,
      padding: '10px 16px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: workspaceColors.accentPrimary,
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      fontSize: 14,
      fontWeight: 600,
      transition: 'all 0.15s ease',
      position: 'relative',
    },
    filesBadge: {
      position: 'absolute',
      top: -6,
      right: -6,
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      backgroundColor: 'white',
      color: workspaceColors.accentPrimary,
      fontSize: 10,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    regenerateBtn: {
      flex: 1,
      padding: '10px 12px',
      borderRadius: 8,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: workspaceColors.borderColor,
      backgroundColor: workspaceColors.bgSecondary,
      color: workspaceColors.textPrimary,
      cursor: canRegenerate ? 'pointer' : 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: canRegenerate ? 1 : 0.4,
      transition: 'all 0.15s ease',
      position: 'relative',
    },
    regenerateCounter: {
      position: 'absolute',
      top: -6,
      right: -6,
      width: 18,
      height: 18,
      borderRadius: '50%',
      backgroundColor: canRegenerate ? workspaceColors.accentPrimary : workspaceColors.textTertiary,
      color: 'white',
      fontSize: 10,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    spinner: {
      animation: 'resultCardSpin 1s linear infinite',
    },
  };

  // Render file item
  const renderFileItem = (file, index) => {
    const FileTypeIcon = FILE_TYPE_ICONS[file.type] || File;
    const iconColor = FILE_TYPE_COLORS[file.type] || '#757575';
    const isImage = file.type === 'image';
    const showImagePreview = isImage && !imageError[index];

    return (
      <div
        key={`${file.url}-${index}`}
        style={cardStyles.fileItem}
        onClick={(e) => handleOpenFile(file, e)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = workspaceColors.bgSecondary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = workspaceColors.bgPrimary;
        }}
        title={`Abrir ${file.filename}`}
      >
        {/* Icon or Image Preview */}
        {showImagePreview ? (
          <img
            src={file.url}
            alt={file.filename}
            style={cardStyles.fileImagePreview}
            onError={() => setImageError(prev => ({ ...prev, [index]: true }))}
          />
        ) : (
          <div 
            style={{
              ...cardStyles.fileIcon,
              backgroundColor: `${iconColor}20`,
            }}
          >
            <FileTypeIcon size={18} color={iconColor} />
          </div>
        )}
        
        {/* File Info */}
        <div style={cardStyles.fileInfo}>
          <div style={cardStyles.fileName}>{file.filename}</div>
          <div style={cardStyles.fileType}>{file.type}</div>
        </div>
        
        {/* Open Icon */}
        <div style={cardStyles.fileAction}>
          <ExternalLink size={14} />
        </div>
      </div>
    );
  };

  return (
    <div style={cardStyles.container}>
      {/* Header */}
      <div style={cardStyles.header}>
        <div style={cardStyles.languageInfo}>
          <span style={cardStyles.languageIcon}>
            <Languages size={14} />
          </span>
          <span style={cardStyles.languageName}>{result.language?.name || 'IA'}</span>
        </div>
        <div style={cardStyles.headerActions}>
          <span style={cardStyles.badge}>
            <ActionIcon size={12} />
            {actionConfig?.badgeLabel || result.action}
          </span>
          {onRemove && (
            <button
              style={cardStyles.removeBtn}
              onClick={handleRemove}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = workspaceColors.danger;
                e.currentTarget.style.backgroundColor = `${workspaceColors.danger}15`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = workspaceColors.textTertiary;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title={
                isHistoryItem
                  ? "Excluir definitivamente do histórico"
                  : "Fechar (move para o histórico)"
              }
            >
              {isHistoryItem ? <Trash2 size={16} /> : <X size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Body - Text */}
      <div style={cardStyles.body}>
        <p style={cardStyles.text}>{result.text}</p>
      </div>

      {/* Files Section - só aparece se tiver arquivos */}
      {hasFiles && (
        <div style={cardStyles.filesSection}>
          <div style={cardStyles.filesHeader}>
            <Paperclip size={12} />
            <span>{files.length} arquivo{files.length > 1 ? 's' : ''} anexado{files.length > 1 ? 's' : ''}</span>
          </div>
          <div style={cardStyles.filesList}>
            {files.slice(0, 3).map((file, index) => renderFileItem(file, index))}
            {files.length > 3 && (
              <div style={{
                fontSize: 11,
                color: workspaceColors.textTertiary,
                textAlign: 'center',
                padding: '4px 0',
              }}>
                + {files.length - 3} arquivo{files.length - 3 > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={cardStyles.actions}>
        {/* Main button: Usar resposta */}
        <button
          style={cardStyles.useBtn}
          onClick={handleUseResponse}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          title={hasFiles ? `Usar resposta + ${files.length} arquivo(s)` : 'Enviar para o campo de texto'}
        >
          <ArrowDownToLine size={18} />
          Usar resposta
          {/* Files badge */}
          {hasFiles && (
            <span style={cardStyles.filesBadge}>
              <Paperclip size={10} />
              {files.length}
            </span>
          )}
        </button>
        
        {/* Regenerate button - only for non-history items */}
        {!isHistoryItem && (
          <button
            style={cardStyles.regenerateBtn}
            onClick={handleRegenerate}
            disabled={!canRegenerate}
            onMouseEnter={(e) => {
              if (canRegenerate) {
                e.currentTarget.style.backgroundColor = workspaceColors.bgTertiary;
                e.currentTarget.style.borderColor = workspaceColors.accentPrimary;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = workspaceColors.bgSecondary;
              e.currentTarget.style.borderColor = workspaceColors.borderColor;
            }}
            title={canRegenerate 
              ? `Regenerar (${remainingRegenerations} restantes)` 
              : 'Limite de regenerações atingido'
            }
          >
            {isRegenerating ? (
              <Loader2 size={18} style={cardStyles.spinner} />
            ) : (
              <RefreshCcw size={18} />
            )}
            <span style={cardStyles.regenerateCounter}>
              {remainingRegenerations}
            </span>
          </button>
        )}
      </div>

      <style>{`
        @keyframes resultCardSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ResultCard;